import { useState, useEffect } from 'react';

interface FormScheduleSettingsProps {
  formId: string;
  isOpen: boolean;
  onClose: () => void;
}

interface ScheduleData {
  schedule: {
    opensAt: string | null;
    closesAt: string | null;
    closeMessage: string | null;
    maxSubmissions: number | null;
    maxSubmissionsPerUser: number | null;
    isScheduled: boolean;
  } | null;
  isOpen: boolean;
  status: 'open' | 'scheduled' | 'closed';
}

export default function FormScheduleSettings({ formId, isOpen, onClose }: FormScheduleSettingsProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Schedule form state
  const [opensAt, setOpensAt] = useState<string>('');
  const [closesAt, setClosesAt] = useState<string>('');
  const [closeMessage, setCloseMessage] = useState<string>('');
  const [maxSubmissions, setMaxSubmissions] = useState<string>('');
  const [maxSubmissionsPerUser, setMaxSubmissionsPerUser] = useState<string>('');

  // Current status
  const [currentStatus, setCurrentStatus] = useState<'open' | 'scheduled' | 'closed'>('open');

  useEffect(() => {
    if (isOpen && formId) {
      loadSchedule();
    }
  }, [isOpen, formId]);

  async function loadSchedule() {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/forms/${formId}/schedule`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to load schedule');
      }

      const data = await response.json() as ScheduleData;

      if (data.schedule) {
        setOpensAt(data.schedule.opensAt ? formatDateTimeLocal(data.schedule.opensAt) : '');
        setClosesAt(data.schedule.closesAt ? formatDateTimeLocal(data.schedule.closesAt) : '');
        setCloseMessage(data.schedule.closeMessage || '');
        setMaxSubmissions(data.schedule.maxSubmissions?.toString() || '');
        setMaxSubmissionsPerUser(data.schedule.maxSubmissionsPerUser?.toString() || '');
      } else {
        setOpensAt('');
        setClosesAt('');
        setCloseMessage('');
        setMaxSubmissions('');
        setMaxSubmissionsPerUser('');
      }
      setCurrentStatus(data.status);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  function formatDateTimeLocal(isoString: string): string {
    const date = new Date(isoString);
    const offset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - offset * 60000);
    return localDate.toISOString().slice(0, 16);
  }

  async function saveSchedule() {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await fetch(`/api/forms/${formId}/schedule`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          opensAt: opensAt ? new Date(opensAt).toISOString() : null,
          closesAt: closesAt ? new Date(closesAt).toISOString() : null,
          closeMessage: closeMessage || null,
          maxSubmissions: maxSubmissions ? parseInt(maxSubmissions) : null,
          maxSubmissionsPerUser: maxSubmissionsPerUser ? parseInt(maxSubmissionsPerUser) : null,
        }),
      });

      if (!response.ok) {
        const result = await response.json() as { error?: string };
        throw new Error(result.error || 'Failed to save schedule');
      }

      setSuccess('Schedule saved successfully');
      await loadSchedule();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function clearSchedule() {
    if (!confirm('Are you sure you want to remove all scheduling settings?')) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const response = await fetch(`/api/forms/${formId}/schedule`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to clear schedule');
      }

      setOpensAt('');
      setClosesAt('');
      setCloseMessage('');
      setMaxSubmissions('');
      setMaxSubmissionsPerUser('');
      setSuccess('Schedule cleared');
      await loadSchedule();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear');
    } finally {
      setSaving(false);
    }
  }

  function getStatusBadge() {
    switch (currentStatus) {
      case 'open':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Open</span>;
      case 'scheduled':
        return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700">Scheduled</span>;
      case 'closed':
        return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">Closed</span>;
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Form Schedule</h2>
              <p className="text-sm text-gray-500">Set open/close times and limits</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {getStatusBadge()}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Messages */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              {success}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Open Date/Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Opens At (optional)
                </label>
                <input
                  type="datetime-local"
                  value={opensAt}
                  onChange={(e) => setOpensAt(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Form will automatically become available at this time
                </p>
              </div>

              {/* Close Date/Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Closes At (Deadline)
                </label>
                <input
                  type="datetime-local"
                  value={closesAt}
                  onChange={(e) => setClosesAt(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Form will automatically close and stop accepting submissions
                </p>
              </div>

              {/* Close Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Closed Message (optional)
                </label>
                <textarea
                  value={closeMessage}
                  onChange={(e) => setCloseMessage(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="This form is no longer accepting responses."
                />
                <p className="mt-1 text-xs text-gray-500">
                  Message shown to users when the form is closed
                </p>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-medium text-gray-900 mb-4">Submission Limits</h3>
              </div>

              {/* Max Submissions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Maximum Total Submissions
                </label>
                <input
                  type="number"
                  min="1"
                  value={maxSubmissions}
                  onChange={(e) => setMaxSubmissions(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Unlimited"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Form will close after reaching this many submissions
                </p>
              </div>

              {/* Max Per User */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Maximum Submissions Per User
                </label>
                <input
                  type="number"
                  min="1"
                  value={maxSubmissionsPerUser}
                  onChange={(e) => setMaxSubmissionsPerUser(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Unlimited"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Limit how many times a single user can submit (based on IP or login)
                </p>
              </div>

              {/* Info box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex gap-3">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">How scheduling works</p>
                    <p className="mt-1 text-blue-700">
                      The system checks for scheduled tasks periodically based on user activity.
                      When a deadline is reached, the form will be automatically archived and stop accepting submissions.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between">
          <button
            onClick={clearSchedule}
            disabled={saving || loading}
            className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
          >
            Clear Schedule
          </button>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={saveSchedule}
              disabled={saving || loading}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Saving...' : 'Save Schedule'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
